import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { KeyRound, ShieldAlert, ArrowLeft, LogOut } from "lucide-react";

// Écran de changement de code PIN — mode OBLIGATOIRE (must_change_pin) ou volontaire.
export default function ChangePinScreen() {
  const { user, changePin, logout } = useAuth();
  const { t } = useApp();
  const navigate = useNavigate();
  const forced = !!user?.must_change_pin;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const digits = (v) => v.replace(/[^0-9]/g, "").slice(0, 6);

  const submit = async (e) => {
    e?.preventDefault();
    if (next.length !== 6) { toast.error(t("pin_need_6")); return; }
    if (next !== confirm) { toast.error(t("pin_mismatch")); return; }
    setBusy(true);
    try {
      await changePin(next, forced ? null : current);
      toast.success(t("change_pin_ok"));
      if (!forced) navigate("/more");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login_error"));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full h-14 rounded-2xl bg-white text-[#1A1A1A] px-5 text-lg tracking-[0.3em] text-center outline-none border border-white/10 focus:ring-2 focus:ring-[#E6D5B8]";

  return (
    <div className="min-h-screen bg-[#0A4D3C] text-white relative overflow-hidden flex flex-col" data-testid="change-pin-screen">
      <div className="absolute inset-0 zellige-overlay pointer-events-none" />
      <div className="relative z-10 p-5 flex items-center justify-between">
        {!forced ? (
          <button onClick={() => navigate(-1)} data-testid="change-pin-back"
            className="text-sm text-white/70 hover:text-white flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t("back") || "Retour"}
          </button>
        ) : <span />}
        {forced && (
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="change-pin-logout"
            className="text-sm text-white/70 hover:text-white flex items-center gap-1.5">
            <LogOut className="h-4 w-4" /> {t("logout")}
          </button>
        )}
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 max-w-md w-full mx-auto pb-16">
        <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6">
          {forced ? <ShieldAlert className="h-9 w-9" /> : <KeyRound className="h-9 w-9" />}
        </div>
        <h1 className="text-3xl font-black tracking-tight mb-2">
          {forced ? t("change_pin_required_title") : t("change_pin_title")}
        </h1>
        <p className="text-white/70 mb-8">
          {forced ? t("change_pin_required_sub") : t("pin_policy_hint")}
        </p>
        <form onSubmit={submit} className="space-y-3">
          {!forced && (
            <input type="password" inputMode="numeric" value={current} onChange={(e) => setCurrent(digits(e.target.value))}
              placeholder={t("current_pin_label")} data-testid="change-pin-current" className={field} />
          )}
          <input type="password" inputMode="numeric" value={next} onChange={(e) => setNext(digits(e.target.value))}
            placeholder={t("new_pin_label")} data-testid="change-pin-new" className={field} />
          <input type="password" inputMode="numeric" value={confirm} onChange={(e) => setConfirm(digits(e.target.value))}
            placeholder={t("confirm_pin_label")} data-testid="change-pin-confirm" className={field} />
          <p className="text-xs text-white/50 px-1">{t("pin_policy_hint")}</p>
          <button type="submit" disabled={busy} data-testid="change-pin-submit"
            className="w-full h-14 rounded-2xl bg-[#E6D5B8] text-[#0A4D3C] font-bold text-lg disabled:opacity-60">
            {busy ? "…" : t("change_pin_cta")}
          </button>
        </form>
      </div>
    </div>
  );
}
